import React, { useEffect, useMemo, useRef, useState } from 'react';
import { City } from 'country-state-city';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { SearchableSelect } from '../ui/searchable-select';
import { getCountryIsoCode, useCanonicalCountryOptions } from '../../utils/countries';
import { Upload, X } from 'lucide-react';
import { uploadsApi } from '../../services/api';
import { toast } from 'sonner';
const DEFAULT_WAREHOUSING_FORM = {
  scope: [],
  location: { city: '', country: '', address: '' },
  startDate: '',
  duration: '',
  durationUnit: 'months',
  spaceRequired: '',
  spaceUnit: 'sqm',
  storageType: 'Standard',
  commodity: '',
  isStackable: 'yes',
  stackHeight: '',
  isHazardous: false,
  hazClass: '',
  unNumber: '',
  temperatureControlled: false,
  tempRange: '',
  humidityControlled: false,
  humidityRange: '',
  securityLevel: 'Standard',
  accessRequirements: [],
  handlingServices: [],
  palletCount: '',
  palletType: 'Euro',
  insuranceRequired: false,
  insuranceValue: '',
  inventoryManagement: false,
  specialRequirements: '',
  packingListFile: null
};
const DURATION_UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];
const SPACE_UNIT_OPTIONS = [
  { value: 'sqm', label: 'm2' },
  { value: 'sqft', label: 'ft2' },
  { value: 'pallets', label: 'Pallets' },
];
const STACKABLE_OPTIONS = [
  { value: 'yes', label: 'Yes - Stackable' },
  { value: 'no', label: 'No - Not Stackable' },
  { value: 'limited', label: 'Limited Stacking' },
];

const WarehousingForm = ({ onSubmit, onCancel, commodities = [], initialData = null, formId = null, scopeOptions: scopeOptionsProp = [], onFormDataChange, validationErrors = {}, clearValidationError }) => {
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState(() => {
    if (initialData && typeof initialData === 'object') {
      return { ...DEFAULT_WAREHOUSING_FORM, ...initialData };
    }
    return { ...DEFAULT_WAREHOUSING_FORM };
  });
  const scopeOptions = scopeOptionsProp && scopeOptionsProp.length > 0 ? scopeOptionsProp : [];
  const didReportInitialScope = useRef(false);
  const countryList = useCanonicalCountryOptions();
  const countryIsoCode = useMemo(() => {
    return getCountryIsoCode(formData.location.country);
  }, [formData.location.country]);
  const cityList = useMemo(() => {
    if (!countryIsoCode) return [];
    return City.getCitiesOfCountry(countryIsoCode).map((city) => ({ value: city.name, label: city.name }));
  }, [countryIsoCode]);
  const commodityOptions = useMemo(() => (
    commodities.map((comm) => {
      const value = typeof comm === 'object' ? comm.name || String(comm) : comm;
      return { value, label: value };
    })
  ), [commodities]);
  const cityIsOther = formData.location.city && !cityList.some((city) => city.value === formData.location.city);
  const citySelectValue = cityIsOther ? '__other__' : (formData.location.city || '');

  // Keep the parent dialog's scope state in sync, especially for CLONE/edit where scope may already be selected.
  useEffect(() => {
    if (!didReportInitialScope.current && onFormDataChange) {
      didReportInitialScope.current = true;
      onFormDataChange({ scope: formData.scope || [] });
    }
  }, [formData.scope, onFormDataChange]);
  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);
  const storageTypes = ['Standard', 'Bonded', 'Temperature Controlled', 'Hazmat', 'High Security', 'Covered', 'Uncovered'];
  const securityLevels = ['Basic', 'Standard', 'Enhanced', 'Maximum'];
  const accessReqs = ['24/7 Access', 'Business Hours Only', 'Prior Appointment', 'Restricted Access'];
  const handlingServices = ['Loading/Unloading', 'Palletization', 'Labeling', 'Sorting', 'Repacking', 'Quality Inspection'];
  const palletTypes = ['Euro', 'Standard', 'Custom'];
  const storageTypeOptions = storageTypes.map((type) => ({ value: type, label: type }));
  const hazardClassOptions = [
    { value: 'Class 1', label: 'Class 1 - Explosives' },
    { value: 'Class 2', label: 'Class 2 - Gases' },
    { value: 'Class 3', label: 'Class 3 - Flammable Liquids' },
    { value: 'Class 4', label: 'Class 4 - Flammable Solids' },
    { value: 'Class 5', label: 'Class 5 - Oxidizing Substances' },
    { value: 'Class 6', label: 'Class 6 - Toxic Substances' },
    { value: 'Class 7', label: 'Class 7 - Radioactive' },
    { value: 'Class 8', label: 'Class 8 - Corrosives' },
    { value: 'Class 9', label: 'Class 9 - Miscellaneous' },
  ];
  const securityLevelOptions = securityLevels.map((level) => ({ value: level, label: level }));
  const palletTypeOptions = palletTypes.map((type) => ({ value: type, label: type }));

  const packingListInputRef = useRef(null);
  const [uploadingPackingList, setUploadingPackingList] = useState(false);

  const handlePackingListUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Packing list file must be under 5MB.');
      event.target.value = '';
      return;
    }
    setUploadingPackingList(true);
    try {
      const res = await uploadsApi.uploadFile(file, 'quote_document');
      const url = res?.data?.file?.url || res?.data?.file_url || res?.data?.url;
      const name = res?.data?.file?.original_filename || res?.data?.original_filename || file.name;
      if (url) {
        setFormData((prev) => ({ ...prev, packingListFile: { name, url } }));
        toast.success('Packing list uploaded.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload packing list.');
    } finally {
      setUploadingPackingList(false);
      event.target.value = '';
    }
  };

  const removePackingListFile = () => {
    setFormData((prev) => ({ ...prev, packingListFile: null }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleCountryChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        country: value || '',
        city: ''
      }
    }));
    if (value?.trim()) clearValidationError?.('location.country');
    clearValidationError?.('location.city');
  };

  const handleCityChange = (value) => {
    if (value === '__other__') {
      setFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          city: ''
        }
      }));
      clearValidationError?.('location.city');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        city: value || ''
      }
    }));
    if (value?.trim()) clearValidationError?.('location.city');
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6" noValidate>
      {scopeOptions.length > 0 && (
        <div>
          <Label>Scope (Select all that apply)</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            {scopeOptions.map((scope) => (
              <label key={scope} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={(formData.scope || []).includes(scope)}
                  onChange={() => {
                    const current = formData.scope || [];
                    const newScopes = current.includes(scope) ? current.filter(s => s !== scope) : [...current, scope];
                    setFormData({ ...formData, scope: newScopes });
                    onFormDataChange?.({ scope: newScopes });
                    clearValidationError?.('scope');
                  }}
                  className="rounded"
                />
                <span className="text-sm">{scope}</span>
              </label>
            ))}
          </div>
          {validationErrors.scope && <p className="mt-1 text-sm text-red-600">{validationErrors.scope}</p>}
        </div>
      )}
      {/* Location */}
      <div>
        <h3 className="font-semibold text-lg mb-4">Warehouse Location</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Country</Label>
            <SearchableSelect
              options={countryList}
              value={formData.location.country}
              onChange={handleCountryChange}
              placeholder="Search or select country"
              allowOther={false}
              error={!!validationErrors['location.country']}
            />
            {validationErrors['location.country'] && <p className="mt-1 text-sm text-red-600">{validationErrors['location.country']}</p>}
          </div>
          <div>
            <Label>City</Label>
            <SearchableSelect
              options={cityList}
              value={citySelectValue}
              onChange={handleCityChange}
              placeholder={countryIsoCode ? 'Search or select city' : 'Select country first'}
              allowOther={!!countryIsoCode}
              disabled={!countryIsoCode}
              error={!!validationErrors['location.city']}
            />
            {citySelectValue === '__other__' && (
              <Input
                className={`mt-2 ${validationErrors['location.city'] ? 'border-red-500' : ''}`}
                value={formData.location.city}
                onChange={(e) => setFormData({ ...formData, location: { ...formData.location, city: e.target.value } })}
                onInput={(e) => {
                  if (e.target.value?.trim()) clearValidationError?.('location.city');
                }}
                placeholder="Type city"
              />
            )}
            {validationErrors['location.city'] && <p className="mt-1 text-sm text-red-600">{validationErrors['location.city']}</p>}
          </div>
          <div className="md:col-span-2">
            <Label>Specific Address (Optional)</Label>
            <Input
              value={formData.location.address}
              onChange={(e) => setFormData({ ...formData, location: { ...formData.location, address: e.target.value } })}
              placeholder="Leave blank if flexible"
            />
          </div>
        </div>
      </div>

      {/* Duration & Space */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Space Requirements</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Start Date</Label>
            <Input
              type="date"
              min={today}
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              onInput={(e) => {
                if (e.target.value) clearValidationError?.('startDate');
              }}
              className={validationErrors.startDate ? 'border-red-500' : ''}
              required
            />
            {validationErrors.startDate && <p className="mt-1 text-sm text-red-600">{validationErrors.startDate}</p>}
          </div>
          <div>
            <Label>Duration</Label>
            <div className="flex space-x-2">
              <Input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                onInput={(e) => {
                  if (e.target.value) clearValidationError?.('duration');
                }}
                placeholder="0"
                className={validationErrors.duration ? 'border-red-500' : ''}
                required
              />
              {validationErrors.duration && <p className="mt-1 text-sm text-red-600">{validationErrors.duration}</p>}
              <SearchableSelect
                options={DURATION_UNIT_OPTIONS}
                className="w-28"
                value={formData.durationUnit}
                onChange={(value) => setFormData({ ...formData, durationUnit: value || 'months' })}
                placeholder="Unit"
                allowOther={false}
              />
            </div>
          </div>
          <div>
            <Label>Space Required</Label>
            <div className="flex space-x-2">
              <Input
                type="number"
                value={formData.spaceRequired}
                onChange={(e) => setFormData({ ...formData, spaceRequired: e.target.value })}
                onInput={(e) => {
                  if (e.target.value) clearValidationError?.('spaceRequired');
                }}
                placeholder="0"
                className={validationErrors.spaceRequired ? 'border-red-500' : ''}
                required
              />
              {validationErrors.spaceRequired && <p className="mt-1 text-sm text-red-600">{validationErrors.spaceRequired}</p>}
              <SearchableSelect
                options={SPACE_UNIT_OPTIONS}
                className="w-24"
                value={formData.spaceUnit}
                onChange={(value) => setFormData({ ...formData, spaceUnit: value || 'sqm' })}
                placeholder="Unit"
                allowOther={false}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Storage Type</Label>
            <SearchableSelect
              options={storageTypeOptions}
              value={formData.storageType}
              onChange={(value) => setFormData({ ...formData, storageType: value || 'Standard' })}
              placeholder="Search or select storage type"
              allowOther={false}
            />
          </div>
          <div>
            <Label>Commodity</Label>
            <SearchableSelect
              options={commodityOptions}
              value={formData.commodity}
              onChange={(value) => {
                setFormData({ ...formData, commodity: value || '' });
                if (value) clearValidationError?.('commodity');
              }}
              placeholder="Search or select commodity"
              allowOther={false}
              error={!!validationErrors.commodity}
            />
            {validationErrors.commodity && <p className="mt-1 text-sm text-red-600">{validationErrors.commodity}</p>}
          </div>
        </div>
      </div>

      {/* Stacking & Hazardous */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Cargo Characteristics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Stackable</Label>
            <SearchableSelect
              options={STACKABLE_OPTIONS}
              value={formData.isStackable}
              onChange={(value) => setFormData({ ...formData, isStackable: value || 'yes' })}
              placeholder="Search or select stackable option"
              allowOther={false}
            />
          </div>
          {formData.isStackable !== 'no' && (
            <div>
              <Label>Maximum Stack Height</Label>
              <Input
                type="number"
                value={formData.stackHeight}
                onChange={(e) => setFormData({ ...formData, stackHeight: e.target.value })}
                placeholder="e.g., 6"
              />
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.isHazardous}
              onChange={(e) => setFormData({ ...formData, isHazardous: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm font-medium">Hazardous Material</span>
          </label>
        </div>

        {formData.isHazardous && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-red-50 rounded-lg">
            <div>
              <Label>UN Number</Label>
              <Input
                value={formData.unNumber}
                onChange={(e) => setFormData({ ...formData, unNumber: e.target.value })}
                placeholder="e.g., UN1234"
                required={formData.isHazardous}
              />
            </div>
            <div>
              <Label>Hazard Class</Label>
              <SearchableSelect
                options={hazardClassOptions}
                value={formData.hazClass}
                onChange={(value) => setFormData({ ...formData, hazClass: value || '' })}
                placeholder="Search or select hazard class"
                allowOther={false}
              />
            </div>
          </div>
        )}
      </div>

      {/* Environmental Controls */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Environmental Controls</h3>
        <div className="space-y-4">
          <div>
            <label className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={formData.temperatureControlled}
                onChange={(e) => setFormData({ ...formData, temperatureControlled: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Temperature Controlled</span>
            </label>
            {formData.temperatureControlled && (
              <Input
                value={formData.tempRange}
                onChange={(e) => setFormData({ ...formData, tempRange: e.target.value })}
                placeholder="e.g., 15-25°C or 2-8°C"
              />
            )}
          </div>
          <div>
            <label className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={formData.humidityControlled}
                onChange={(e) => setFormData({ ...formData, humidityControlled: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Humidity Controlled</span>
            </label>
            {formData.humidityControlled && (
              <Input
                value={formData.humidityRange}
                onChange={(e) => setFormData({ ...formData, humidityRange: e.target.value })}
                placeholder="e.g., 40-60%"
              />
            )}
          </div>
        </div>
      </div>

      {/* Access & Security */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Access & Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Security Level</Label>
            <SearchableSelect
              options={securityLevelOptions}
              value={formData.securityLevel}
              onChange={(value) => setFormData({ ...formData, securityLevel: value || 'Standard' })}
              placeholder="Search or select security level"
              allowOther={false}
            />
          </div>
        </div>

        <div className="mt-4">
          <Label>Access Requirements</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {accessReqs.map((req) => (
              <label key={req} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={formData.accessRequirements.includes(req)}
                  onChange={(e) => {
                    const newReqs = e.target.checked
                      ? [...formData.accessRequirements, req]
                      : formData.accessRequirements.filter(r => r !== req);
                    setFormData({ ...formData, accessRequirements: newReqs });
                  }}
                  className="rounded"
                />
                <span className="text-sm">{req}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Services */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Additional Services</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {handlingServices.map((service) => (
            <label key={service} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.handlingServices.includes(service)}
                onChange={(e) => {
                  const newServices = e.target.checked
                    ? [...formData.handlingServices, service]
                    : formData.handlingServices.filter(s => s !== service);
                  setFormData({ ...formData, handlingServices: newServices });
                }}
                className="rounded"
              />
              <span className="text-sm">{service}</span>
            </label>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label>Pallet Count</Label>
            <Input
              type="number"
              value={formData.palletCount}
              onChange={(e) => setFormData({ ...formData, palletCount: e.target.value })}
              placeholder="0"
            />
          </div>
          <div>
            <Label>Pallet Type</Label>
            <SearchableSelect
              options={palletTypeOptions}
              value={formData.palletType}
              onChange={(value) => setFormData({ ...formData, palletType: value || 'Euro' })}
              placeholder="Search or select pallet type"
              allowOther={false}
            />
          </div>
        </div>

        <div className="flex space-x-6 mt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.insuranceRequired}
              onChange={(e) => setFormData({
                ...formData,
                insuranceRequired: e.target.checked,
                insuranceValue: e.target.checked ? formData.insuranceValue : ''
              })}
              className="rounded"
            />
            <span className="text-sm font-medium">Insurance Required</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.inventoryManagement}
              onChange={(e) => setFormData({ ...formData, inventoryManagement: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm font-medium">Inventory Management System</span>
          </label>
        </div>

        {formData.insuranceRequired && (
          <div className="max-w-md mt-4">
            <Label>Invoice Value (USD)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.insuranceValue}
              onChange={(e) => setFormData({ ...formData, insuranceValue: e.target.value })}
              placeholder="Enter insured invoice value"
              required={formData.insuranceRequired}
            />
          </div>
        )}
      </div>

      {/* Packing List Upload */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Documents</h3>
        <div>
          <Label>Upload Packing List</Label>
          <div className="mt-2">
            {!formData.packingListFile ? (
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => packingListInputRef.current?.click()}
              >
                <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                <p className="text-sm text-gray-500">
                  {uploadingPackingList ? 'Uploading...' : 'Click to upload packing list (PDF, Word, Excel, max 5MB)'}
                </p>
                <input
                  ref={packingListInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handlePackingListUpload}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-2">
                  <Upload size={16} className="text-blue-600" />
                  <span className="text-sm text-blue-900">{formData.packingListFile.name}</span>
                </div>
                <button
                  type="button"
                  onClick={removePackingListFile}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

    </form>
  );
};

export default WarehousingForm;
